import {createContext, use} from 'react'

/**
 * Row-level treatments a consumer wants applied to the event's `Timeline.Item`
 * `<li>` that `TimelineRow` renders deep in this package. Under
 * `primer_react_timeline_list_semantics`, issue-viewer can no longer wrap the
 * event in an outer `<div>` (an `<ol>` accepts only `<li>` children), so it
 * feeds these values through this context instead. `RowBase` reads them and
 * applies them directly on the `<li>`.
 *
 * When no provider is present (the flag-off path, Storybook, or any other
 * consumer), `use` returns `undefined` and `RowBase` behaves exactly as it did
 * before, so the production DOM stays byte-for-byte unchanged.
 */
export type TimelineRowPassthrough = {
  /** Extra class name merged onto the event `<li>` (for example row-box padding). */
  rowClassName?: string
  /**
   * `data-*` attributes spread onto the event `<li>` (for example
   * `data-timeline-event-id`, `data-highlighted-event`, `data-wrapper-timeline-id`).
   * Keys with an `undefined` value are omitted.
   */
  rowDataAttributes?: Record<`data-${string}`, string | number | boolean | undefined>
  /**
   * Marks the event `<li>` as the start of a new event group. `RowBase` adds
   * `TimelineRow.module.css`'s `.Timeline_Item_groupStart`, whose top padding
   * reproduces the flag-off group-boundary spacing (the divider strip that used to
   * sit above the row) as padding on the `<li>` itself, rather than a full-width
   * child injected into the row (which broke Primer's flex and grid layouts).
   */
  isGroupStart?: boolean
  /**
   * Opts the event `<li>` into the consumer-driven left indent. When `true`,
   * `RowBase` adds `TimelineRow.module.css`'s `.Timeline_Item_indent`, whose
   * `margin-left` reads the `--timeline-row-indent-left` custom property the
   * consumer supplies through `rowClassName`. The winning declaration lives in
   * this package (co-located with `.Timeline_Item_inlineAvatars`) so it beats
   * Primer's `Timeline.Item` margin by the same guaranteed source order, instead
   * of relying on the consumer's bundle order or a specificity hack. The
   * consumer-specific pixel values stay in the consumer's CSS, carried in the
   * custom property (which never collides with Primer, since Primer does not set
   * it).
   */
  rowIndent?: boolean
}

export const TimelineRowPassthroughContext = createContext<TimelineRowPassthrough | undefined>(undefined)

export function useTimelineRowPassthrough(): TimelineRowPassthrough | undefined {
  return use(TimelineRowPassthroughContext)
}
